import { Component, OnInit, Input, HostListener, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { interval, Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { TranslateService } from '@services/translate.service';
import { LocalizationService } from '@services/localization.service';
import { ApiService } from '@services/api.service';
import { UiHelper } from '@helpers/ui.helper';
import { UserHelper } from '@helpers/user.helper';
import { environment } from '@environments/environment';
import { IsoCountriesPipe } from '@pipes/isoCountries.pipe';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DimmerService } from '@services/dimmer.service';
import { sportsSvgList } from '@app/constants/svgUrl.constant';

@Component({
  selector: 'spinner-utils',
  styleUrls: ['./utils.sass'],
  template: ` <ng-container>
    <ng-container *ngIf="spinnerState; else propsBody">
      <ng-container [ngSwitch]="spinnerType">
        <ng-container *ngSwitchCase="'ellipsis'">
          <div class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </ng-container>
        <ng-container *ngSwitchDefault>
          <div class="theme-button navbar-right-side-item">
            <div class="lds-spinner">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </ng-container>
      </ng-container>
    </ng-container>
    <ng-template #propsBody>
      <ng-content></ng-content>
    </ng-template>
  </ng-container>`,
})
export class SpinnerUtils {
  @Input() spinnerState;
  @Input() spinnerType;
}

@Component({
  selector: 'image-loader-utils',
  styleUrls: ['./utils.sass'],
  template: `
    <ng-container *ngIf="this.url || this.customUrl">
      <ng-container [ngSwitch]="this.imageFormat">
        <ng-container *ngSwitchCase="'svg'">
          <svg-icon [src]="this.customUrl || this.svgIcon + '.svg'" [class]="'image-loader ' + this.extraClasses" [id]="this.url"></svg-icon>
        </ng-container>
        <ng-container *ngSwitchCase="'png'">
          <img [src]="this.customUrl || this.assetPath" [alt]="this.altText || this.customUrl || this.url" [class]="'image-loader ' + this.extraClasses" />
        </ng-container>
      </ng-container>
    </ng-container>
  `,
})
export class ImageLoaderUtils implements OnInit, OnChanges {
  public svgIcon: any;
  public assetPath: string;
  @Input() url?: string;
  @Input() customUrl?: string;
  @Input() imageFormat = 'svg';
  @Input() shared?: boolean = true;
  @Input() extraClasses?: string;
  @Input() altText?: string;
  @Input() isFlags?: boolean = false;

  async createIcon() {
    const isoCountry = new IsoCountriesPipe();
    if (this.isFlags) {
      this.assetPath = `${environment.assetsPath}images/shared/flags/${isoCountry.transform(this.url)}`;
      this.svgIcon = `${environment.assetsPath}images/shared/flags/${isoCountry.transform(this.url)}`;
    } else {
      if (environment.assetsOnlyProject) {
        this.assetPath = `${environment.assetsPath}images/${environment.product}/${this.url}`;
        this.svgIcon = `${environment.assetsPath}images/${environment.product}/${this.url}`;
      } else {
        this.assetPath = `${environment.assetsPath}images/${this.shared ? 'shared' : environment.product}/${this.url}`;
        this.svgIcon = `${environment.assetsPath}images/${this.shared ? 'shared' : environment.product}/${this.url}`;
      }
    }
  }

  createImage() {
    if (environment.assetsOnlyProject) {
      this.assetPath = `${environment.assetsPath}images/${environment.product}/${this.url}.png`;
    } else {
      this.assetPath = `${environment.assetsPath}images/${this.shared ? 'shared' : environment.product}/${this.url}.png`;
    }
  }

  ngOnInit() {
    if (this.imageFormat === 'svg') {
      this.createIcon().then();
    } else if (this.imageFormat === 'png') {
      this.createImage();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.url) {
      if (this.imageFormat === 'svg') {
        this.createIcon().then();
      } else if (this.imageFormat === 'png') {
        this.createImage();
      }
    }
  }
}

@Component({
  selector: 'image-loader-flag-utils',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrls: ['./utils.sass'],
  template: `
    <ng-container *ngIf="this.url">
      <ng-container [ngSwitch]="this.imageFormat">
        <ng-container *ngSwitchCase="'svg'">
          <svg-icon [src]="this.customUrl || this.svgIcon + '.svg'" [class]="'image-loader flag-loader ' + this.extraClasses"></svg-icon>
        </ng-container>
        <ng-container *ngSwitchCase="'png'">
          <img [src]="this.customUrl || this.assetPath" [alt]="this.altText || this.customUrl || this.url" [class]="this.extraClasses" class="image-loader-png" />
        </ng-container>
      </ng-container>
    </ng-container>
  `,
})
export class ImageLoaderFlagUtils implements OnInit, OnChanges {
  public svgIcon: any;
  public assetPath: string;
  @Input() url?: string;
  @Input() customUrl?: string;
  @Input() imageFormat = 'svg';
  @Input() shared?: boolean = true;
  @Input() extraClasses?: string;
  @Input() altText?: string;
  @Input() isFlags?: boolean = false;

  async createIcon() {
    const isoCountry = new IsoCountriesPipe();
    if (this.isFlags) {
      this.assetPath = `${environment.assetsPath}images/shared/flags/${isoCountry.transform(this.url)}`;
      this.svgIcon = `${environment.assetsPath}images/shared/flags/${isoCountry.transform(this.url)}`;
    } else {
      if (environment.assetsOnlyProject) {
        this.assetPath = `${environment.assetsPath}images/${environment.product}/${this.url}`;
        this.svgIcon = `${environment.assetsPath}images/${environment.product}/${this.url}`;
      } else {
        this.assetPath = `${environment.assetsPath}images/${this.shared ? 'shared' : environment.product}/${this.url}`;
        this.svgIcon = `${environment.assetsPath}images/${this.shared ? 'shared' : environment.product}/${this.url}`;
      }
    }
  }

  createImage() {
    if (environment.imageFlagUrl) {
      this.assetPath = `${environment.imageFlagUrl}${this.url?.toUpperCase()}.png`;
    } else {
      this.assetPath = `${environment.assetsPath}images/${this.shared ? 'shared' : environment.product}/${this.url}.png`;
    }
  }

  ngOnInit() {
    if (this.imageFormat === 'svg') {
      this.createIcon().then();
    } else if (this.imageFormat === 'png') {
      this.createImage();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.url) {
      if (this.imageFormat === 'svg') {
        this.createIcon().then();
      } else if (this.imageFormat === 'png') {
        this.createImage();
      }
    }
  }
}

@Component({
  selector: 'account-menu-selection-utils',
  styleUrls: ['./utils.sass'],
  animations: [
    trigger('dropdown', [
      state(
        'closed',
        style({
          height: '0',
          opacity: '0',
          overflow: 'hidden',
        })
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: '1',
        })
      ),
      transition('closed <=> open', animate('300ms ease-in-out')),
    ]),
  ],
  template: `
    <div class="account-menu-selection" [ngClass]="{ open: isOpen, active: !isOpen }" tabindex="0" (blur)="onBlur()">
      <div class="selection-box" (click)="toggleCollapse()">
        <ng-container *ngIf="selectedItem; else showplaceholder">
          <div class="selected-item">{{ getSelectedItemName() }}</div>
        </ng-container>
        <ng-template #showplaceholder>
          <div class="placeholder">{{ placeholder }}</div>
        </ng-template>

        <div class="collapsable">
          <span class="icon" [ngClass]="{ open: isOpen, active: !isOpen }">
            <image-loader-utils url="collapse"></image-loader-utils>
          </span>
        </div>
      </div>
      <ul class="dropdown" [@dropdown]="isOpen ? 'open' : 'closed'">
        <li *ngFor="let item of selectionList" (click)="selectItem(item)" [ngClass]="{ 'selected-item': item.id === selectedItem }">
          {{ item.name }}
        </li>
      </ul>
    </div>
  `,
})
export class AccountMenuSelectionUtils {
  @Input() selectedItem: number;
  @Input() selectionList: { id: number; name: string }[];
  @Input() placeholder?: string;
  @Output() selectionChange: EventEmitter<number> = new EventEmitter<number>();
  isOpen = false;
  isFirst = true;

  onBlur() {
    this.isOpen = false;
  }

  toggleCollapse(): void {
    this.isOpen = !this.isOpen;
  }

  selectItem(item: { id: number; name: string }): void {
    this.isFirst = false;
    this.selectedItem = item.id;
    this.selectionChange.emit(this.selectedItem);
    this.isOpen = false;
  }

  getSelectedItemName(): string {
    const selected = this.selectionList.find(item => item.id === this.selectedItem);
    return this.isFirst ? this.placeholder : selected.name;
  }
}
@Component({
  selector: 'account-menu-combo-selection-utils',
  styleUrls: ['./utils.sass'],
  animations: [
    trigger('dropdown', [
      state(
        'closed',
        style({
          height: '0',
          opacity: '0',
          overflow: 'hidden',
        })
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: '1',
        })
      ),
      transition('closed <=> open', animate('300ms ease-in-out')),
    ]),
  ],
  template: `
    <div class="account-menu-selection" [ngClass]="{ open: isOpen, active: !isOpen }" tabindex="0" (blur)="onBlur($event)">
      <div class="selection-box" (click)="toggleCollapse()">
        <input #searchInput *ngIf="isOpen" [(ngModel)]="searchQuery" class="search-input" [placeholder]="'Search' | translate" autofocus (click)="inputTouch($event)" />
        <ng-container *ngIf="selectedItem && !isOpen">
          <div class="selected-item">{{ getSelectedItemName() }}</div>
        </ng-container>
        <ng-container *ngIf="!selectedItem && !isOpen">
          <div class="placeholder">{{ placeholder }}</div>
        </ng-container>

        <div class="collapsable">
          <span class="icon" [ngClass]="{ open: isOpen, active: !isOpen }">
            <image-loader-utils url="collapse"></image-loader-utils>
          </span>
        </div>
      </div>
      <ul class="dropdown" [@dropdown]="isOpen ? 'open' : 'closed'">
        <li *ngIf="filteredList().length === 0" class="not-found">
          {{ 'no_match_found' | translate }}
        </li>
        <li *ngFor="let item of filteredList()" (click)="selectItem(item)" [ngClass]="{ 'selected-item': item.id === selectedItem }">
          {{ item.name }}
        </li>
      </ul>
    </div>
  `,
})
export class AccountMenuComboSelectionUtils {
  @ViewChild('searchInput') searchInputElement: ElementRef;
  @Input() selectedItem: number;
  @Input() selectionList: { id: number; name: string }[];
  @Input() placeholder?: string;
  @Output() selectionChange: EventEmitter<number> = new EventEmitter<number>();
  isOpen = false;
  isFirst = true;
  searchQuery = '';
  listOfSearch = [];

  inputTouch($event: any) {
    $event.stopPropagation();
    this.isOpen = !this.isOpen;
  }
  onBlur(event: FocusEvent) {
    if (!event.relatedTarget || !(event.relatedTarget as Element).classList.contains('search-input')) {
      this.isOpen = false;
    }
  }

  toggleCollapse(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.resetInput();
      setTimeout(() => {
        // Input elementine odaklan
        this.searchInputElement.nativeElement.focus();
      }, 0);
    }
  }

  selectItem(item: { id: number; name: string }): void {
    this.isFirst = false;
    this.selectedItem = item.id;
    this.selectionChange.emit(this.selectedItem);
    this.isOpen = false;
  }

  resetInput() {
    this.searchQuery = '';
  }
  getSelectedItemName(): string {
    const selected = this.selectionList.find(item => item.id === this.selectedItem);
    return this.isFirst ? this.placeholder : selected.name;
  }

  filteredList() {
    if (this.searchQuery === '') {
      return this.selectionList;
    } else {
      this.listOfSearch = this.selectionList.filter(item => item.name.toLowerCase().includes(this.searchQuery.toLowerCase()));

      return this.listOfSearch;
    }
  }
}

@Component({
  selector: 'account-lock-button',
  styleUrls: ['./utils.sass'],
  template: ` <div class="lock-button-main-container text sl-round round-5" *ngIf="this._UserHelper.isSignedIn && this.isMobileView === false">
    <div class="lock-info-text">{{ 'current_slip_core_text_3' | translate }}</div>
    <div class="lock-button-box">
      <div id="accountLockButton24" mat-flat-button class="lock-button">
        {{ '24h_Lock' | translate }}
      </div>
      <section class="account-lock-section" *ngIf="progressValue > 0">
        <ng-container *ngIf="holdSeconds != 0">
          <mat-progress-bar class="account-lock-progress-bar" mode="determinate" value="{{ this.progressValue }}"></mat-progress-bar>
          <div class="progress-bar-text">{{ holdSeconds }}</div>
        </ng-container>
      </section>
    </div>
  </div>`,
})
export class AccountLockButton {
  @Input() isMobileView;
  @Output() holdTime: EventEmitter<number> = new EventEmitter<number>();
  cancel: Observable<string>;
  state: Subject<string> = new Subject<string>();
  holdSeconds = 0;
  progressValue = 0;
  accountLockIsDisabled = false;
  constructor(
    private _LocalizationService: LocalizationService,
    private _TranslateService: TranslateService,
    private _ApiService: ApiService,
    public _UiHelper: UiHelper,
    public _UserHelper: UserHelper
  ) {
    this.cancel = this.state.pipe(
      filter(v => v === 'cancel'),
      tap(v => {
        this.holdTime.emit(0);
        this.holdSeconds = 0;
        this.progressValue = 0;
      })
    );
  }

  @HostListener('mouseup', ['$event'])
  @HostListener('mouseleave', ['$event'])
  onExit() {
    this.state.next('cancel');
  }

  @HostListener('mousedown', ['$event'])
  onHold(e) {
    const target = e.target || e.srcElement || e.currentTarget;
    const idAttr = target.attributes.id;
    if (idAttr == undefined) {
      return '';
    }

    const value = idAttr.nodeValue;
    if (value != 'accountLockButton24') {
      return '';
    }
    this.state.next('start');

    const n = 100;
    interval(n)
      .pipe(
        takeUntil(this.cancel),
        tap(v => {
          const result = v * n;
          this.holdSeconds = result / 1000;
          this.progressValue = (this.holdSeconds / 3) * 100;
          if (result == 3000) {
            this.state.next('cancel');

            this._ApiService.accountLock24().then(result => {
              if (result.code == '') {
                return 'error|System Error';
              } else {
                if (result.code == 'error') {
                  this._UiHelper.errorSnackBar(result.message, '');
                } else if (result.code == 'errorcode') {
                  const t_message = this._TranslateService.instant(result.message);
                  this._UiHelper.errorSnackBar(t_message, '');
                } else if (result.code == 'success') {
                  this._UiHelper.successSnackBar(result.message, '');
                  this.holdSeconds = 0;
                  this.progressValue = 0;
                  this.accountLockIsDisabled = true;
                }
              }
            });
          }

          this.holdTime.emit(result);
        })
      )
      .subscribe();
  }
}
@Component({
  selector: 'social-media-utils',
  styleUrls: ['./utils.sass'],
  template: ` <ng-container [ngSwitch]="[] | getProductName">
    <ng-container *ngSwitchDefault>
      <div *ngIf="('twitterUrl' | getEnv) || ('instagramUrl' | getEnv) || ('facebookUrl' | getEnv) || ('whatsappLink' | getEnv)" class="social-account">
        <a *ngIf="'twitterUrl' | getEnv" [href]="'twitterUrl' | getEnv" target="_blank">
          <image-loader-utils url="twitter" extraClasses="twitter-icon"></image-loader-utils>
        </a>
        <a *ngIf="'instagramUrl' | getEnv" [href]="'instagramUrl' | getEnv" target="_blank">
          <image-loader-utils url="instagram" extraClasses="instagram-icon"></image-loader-utils>
        </a>
        <a *ngIf="'facebookUrl' | getEnv" [href]="'facebookUrl' | getEnv" target="_blank">
          <image-loader-utils url="facebook" extraClasses="facebook-icon"></image-loader-utils>
        </a>
        <a *ngIf="'whatsappUrl' | getEnv" (click)="whatsappLink()" target="_blank">
          <image-loader-utils url="whatsapp" extraClasses="whatsapp-icon"></image-loader-utils>
        </a>
      </div>
    </ng-container>
  </ng-container>`,
})
export class SocialMediaUtils {
  whatsappLink(): void {
    window.open(`https://wa.me/${environment.whatsappUrl}`, '_blank');
  }
  telegramLink(): void {
    window.open(`https://t.me/${environment.whatsappUrl}`, '_blank');
  }
}

@Component({
  selector: 'dimmer-utils',
  styleUrls: ['./utils.sass'],
  template: `<div *ngIf="dimmerState" class="dimmer" id="dimmer" (click)="closeDimmer()"></div>`,
})
export class DimmerUtils implements OnInit {
  dimmerState = false;

  constructor(public _DimmerService: DimmerService) {}

  ngOnInit() {
    this._DimmerService.getActiveModalId().subscribe(id => {
      this.dimmerState = !!id;
    });
  }

  public closeDimmer(): void {
    this._DimmerService.clearActiveModalId();
  }
}

@Component({
  selector: 'team-uniform-utils',
  styleUrls: ['./utils.sass'],
  template: `<img
    [id]="'team-uniform-' + this.teamId"
    [alt]="this.teamId"
    [class]="'team-uniform-image' + ' ' + extraClass"
    [errorImage]="this.createErrorImage()"
    [lazyLoad]="this.imageUrl"
    [ngStyle]="{ opacity: this.isImage ? 1 : 0 }"
    (onStateChange)="myCallbackFunction($event)" />`,
})
export class TeamUniformUtils implements OnChanges {
  public imageUrl: string;
  public isImage = false;
  private teamAssetsPath: string = environment.teamImageAssetsUrl;
  private assetsPath: string = environment.assetsPath;
  private errorImagePath = '00';

  @Input() teamId: number;
  @Input() extraClass: string;
  ngOnChanges(changes: SimpleChanges) {
    if (changes?.teamId.currentValue) {
      this.createImage().then();
    }
  }

  async createImage() {
    if (this.teamId) {
      this.imageUrl = `${this.teamAssetsPath}${this.teamId}.png`;
    }
  }

  public createErrorImage(): string {
    return `${this.teamAssetsPath}${this.errorImagePath}.png`;
  }

  myCallbackFunction(event: any) {
    switch (event.reason) {
      case 'setup':
        // The lib has been instantiated but we have not done anything yet.
        break;
      case 'observer-emit':
        // The image observer (intersection/scroll/custom observer) has emit a value so we
        // should check if the image is in the viewport.
        // `event.data` is the event in this case.
        break;
      case 'start-loading':
        break;
      case 'mount-image':
        // The image has been loaded successfully so lets put it into the DOM
        break;
      case 'loading-succeeded':
        this.isImage = true;
        break;
      case 'loading-failed':
        this.isImage = true;
        break;
      case 'finally':
        break;
    }
  }
}
@Component({
  selector: 'league-utils',
  styleUrls: ['./utils.sass'],
  template: `<img
    [id]="'league-image' + this.leagueId"
    [alt]="this.leagueId"
    [class]="'highlight-mobile' + ' ' + extraClass"
    [errorImage]="this.createErrorImage()"
    [lazyLoad]="this.imageUrl"
    [ngStyle]="{ opacity: this.isImage ? 1 : 0 }"
    (onStateChange)="myCallbackFunction($event)" />`,
})
export class LeagueUtils {
  public imageUrl: string;
  public isImage = false;
  private imageLeagueUrl: string = environment.imageLeagueUrl;
  private assetsPath: string = environment.assetsPath;
  private errorImagePath = '00';

  @Input() leagueId: number;
  @Input() extraClass: string;
  ngOnInit() {
    this.createImage();
  }
  async createImage() {
    if (this.leagueId) {
      this.imageUrl = `${this.imageLeagueUrl}${this.leagueId}.png`;
    }
  }

  public createErrorImage(): string {
    return `${this.imageLeagueUrl}${this.errorImagePath}.png`;
  }

  myCallbackFunction(event: any) {
    switch (event.reason) {
      case 'setup':
        // The lib has been instantiated but we have not done anything yet.
        break;
      case 'observer-emit':
        // The image observer (intersection/scroll/custom observer) has emit a value so we
        // should check if the image is in the viewport.
        // `event.data` is the event in this case.
        break;
      case 'start-loading':
        break;
      case 'mount-image':
        // The image has been loaded successfully so lets put it into the DOM
        break;
      case 'loading-succeeded':
        this.isImage = true;
        break;
      case 'loading-failed':
        this.isImage = true;
        break;
      case 'finally':
        break;
    }
  }
}

@Component({
  selector: 'sport-on-tv-utils',
  styleUrls: ['./utils.sass'],
  template: `
    <div class="sport-on-tv-container">
      <image-loader-utils extraClasses="sport-icon-tv" [url]="sportsSvgList[this.sportId]?.svgKey"></image-loader-utils>
      <image-loader-utils url="live" extraClasses="live-stream-icon"></image-loader-utils>
    </div>
  `,
})
export class SportIconOnTv {
  @Input() sportId: number;

  protected readonly sportsSvgList = sportsSvgList;
}

@Component({
  selector: 'formatted-date',
  template: `
    <div class="day">{{ day }}</div>
    <div class="month">{{ month | translate }}</div>
  `,
  styles: [
    `
      .day {
        color: var(--black-color-800);
        font-size: 20px;
        line-height: 16px;
      }

      .month {
        color: var(--black-color-900);
        font-size: 15px;
      }
    `,
  ],
})
export class FormattedDateComponent implements OnInit {
  @Input() date: Date;
  day: number;
  month: string;
  monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  ngOnInit() {
    this.day = this.date.getDate();
    this.month = this.monthNames[this.date.getMonth()];
  }
}

@Component({
  selector: 'confetti-utils',
  styleUrls: ['./utils.sass'],
  template: `
    <div class="confetti">
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
      <div class="confetti-piece"></div>
    </div>
  `,
})
export class ConfettiUtil {}
